import styled from '/web_modules/styled-components.js';
import React, { useState, useEffect } from '/web_modules/react.js';
import { Separator } from './Separator.js';
import { Song, SongTitle, AlbumTitle } from './Song.js';
import { Score, ScoreIncreaseArrow, ScoreNumber, ScoreContainer, ScoreDecreaseArrow } from './Score.js';
import { NewSongButton } from './NewSongButton.js';
import { AnimatePresence, motion } from '/web_modules/framer-motion.js';
import { client } from './client.js';
import { useMutation, useQuery, queryCache } from '/web_modules/react-query.js';
import { ExpandedSpinner } from './Spinner.js';
import { ResponsiveLayout, Stack, gap } from './Layout.js';
import { appendMatchHistory } from './matchHistory.js';
const PlayViewWrap = styled(motion.section).withConfig({
  displayName: "Play__PlayViewWrap",
  componentId: "sc-1x8cl71-0"
})(["display:flex;flex-direction:column;padding:0 32px;overflow:hidden;h1{text-align:center;margin-bottom:32px;}", "{margin-top:72px;}@media screen and (min-width:800px){", "{", "}", "{flex-grow:1;width:100%;& >:first-child{margin-bottom:32px;}&:last-of-type{flex-direction:column-reverse;& >:last-child{margin-bottom:32px;}}}}"], NewSongButton, ResponsiveLayout, gap('48px', 'row'), Stack);
const SongAScoreContainer = styled(ScoreContainer).withConfig({
  displayName: "Play__SongAScoreContainer",
  componentId: "sc-1x8cl71-1"
})(["transform:translateY(12px);@media screen and (min-width:800px){transform:none;}"]);
const SongBScoreContainer = styled(ScoreContainer).withConfig({
  displayName: "Play__SongBScoreContainer",
  componentId: "sc-1x8cl71-2"
})(["transform:none;"]);

const postNewMatch = () => client('/api/new', {
  method: 'POST'
});

export const getMatch = (key, matchId) => {
  return client(`/api/get-match?id=${matchId}`);
};

const postVote = ({
  matchId,
  winner
}) => client('/api/vote', {
  body: {
    matchId,
    winner
  }
});

const getRatingDiff = rating => rating.new - rating.old;

const prefixSign = n => n <= 0 ? n.toString() : `+${n.toString()}`;

export function PlayView() {
  const [matchId, setMatchId] = useState(() => sessionStorage.getItem('matchId'));
  const [execCreateNewMatch] = useMutation(postNewMatch, {});
  const matchQueryResult = useQuery(['match', matchId ?? ''], getMatch, {
    enabled: matchId,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchInterval: false,
    refetchOnWindowFocus: false
  });
  const [previousMatch, setPreviousMatch] = useState();
  const displayedMatch = matchQueryResult.data ?? previousMatch;
  const match = displayedMatch && {
    id: matchId ?? '',
    a: displayedMatch.a,
    b: displayedMatch.b
  };
  useEffect(() => {
    if (!matchId) {
      // @ts-expect-error - because vs code doesnt detect the new types
      execCreateNewMatch(undefined, {
        onSuccess: ({
          matchId
        }) => {
          setMatchId(matchId);
          sessionStorage.setItem('matchId', matchId);
        }
      });
    }
  }, [execCreateNewMatch, matchId]);

  const vote = async ({
    matchId,
    winner
  }) => {
    displayedMatch && appendMatchHistory({
      a: displayedMatch.a.name,
      b: displayedMatch.b.name,
      winner
    });
    const [voteResult, {
      matchId: nextMatchId
    }] = await Promise.all([postVote({
      matchId,
      winner
    }), postNewMatch()]);
    return { ...voteResult,
      nextMatchId
    };
  };

  const [execVote, voteResult] = useMutation(vote, {
    onSuccess: data => {
      sessionStorage.setItem('matchId', data.nextMatchId);
      queryCache.prefetchQuery(['match', data.nextMatchId], getMatch);
      queryCache.invalidateQueries('rankings');
    }
  });
  const ratings = voteResult.data && {
    a: {
      rating: voteResult.data.a.rating.new,
      pointsDiff: getRatingDiff(voteResult.data.a.rating)
    },
    b: {
      rating: voteResult.data.b.rating.new,
      pointsDiff: getRatingDiff(voteResult.data.b.rating)
    }
  };
  const hasVoted = voteResult.isSuccess || voteResult.isError;
  return matchId && match ? /*#__PURE__*/React.createElement(PlayViewWrap, null, /*#__PURE__*/React.createElement("h1", null, "Pick your favorite!"), /*#__PURE__*/React.createElement(ResponsiveLayout, null, /*#__PURE__*/React.createElement(Stack, null, /*#__PURE__*/React.createElement(Song, {
    onClick: () => execVote({
      matchId,
      winner: 'a'
    }),
    disabled: !voteResult.isIdle
  }, /*#__PURE__*/React.createElement(SongTitle, {
    key: match.a.name
  }, match.a.name), /*#__PURE__*/React.createElement(AlbumTitle, {
    key: match.a.album
  }, match.a.album)), /*#__PURE__*/React.createElement(SongAScoreContainer, null, /*#__PURE__*/React.createElement(AnimatePresence, null, ratings && /*#__PURE__*/React.createElement(Score, {
    initial: {
      y: 30,
      height: 0
    },
    animate: {
      y: 0,
      height: 'auto'
    },
    exit: {
      y: 30,
      height: 0
    }
  }, ratings.a.pointsDiff > 0 ? /*#__PURE__*/React.createElement(ScoreIncreaseArrow, null) : /*#__PURE__*/React.createElement(ScoreDecreaseArrow, null), /*#__PURE__*/React.createElement(ScoreNumber, null, `${ratings.a.rating} (${prefixSign(ratings.a.pointsDiff)})`))))), /*#__PURE__*/React.createElement(Separator, {
    isLoading: voteResult.isLoading
  }), /*#__PURE__*/React.createElement(Stack, null, /*#__PURE__*/React.createElement(SongBScoreContainer, null, /*#__PURE__*/React.createElement(AnimatePresence, null, ratings && /*#__PURE__*/React.createElement(Score, {
    initial: {
      y: -30,
      height: 0
    },
    animate: {
      y: 0,
      height: 'auto'
    },
    exit: {
      y: -30,
      height: 0
    }
  }, ratings.b.pointsDiff > 0 ? /*#__PURE__*/React.createElement(ScoreIncreaseArrow, null) : /*#__PURE__*/React.createElement(ScoreDecreaseArrow, null), /*#__PURE__*/React.createElement(ScoreNumber, null, `${ratings.b.rating} (${prefixSign(ratings.b.pointsDiff)})`)))), /*#__PURE__*/React.createElement(Song, {
    onClick: () => execVote({
      matchId,
      winner: 'b'
    }),
    disabled: !voteResult.isIdle
  }, /*#__PURE__*/React.createElement(SongTitle, {
    key: match.b.name
  }, match.b.name), /*#__PURE__*/React.createElement(AlbumTitle, {
    key: match.b.album
  }, match.b.album)))), /*#__PURE__*/React.createElement(AnimatePresence, null, (hasVoted || match && matchQueryResult.isLoading) && voteResult.data && /*#__PURE__*/React.createElement(NewSongButton, {
    exit: {
      y: '100%',
      opacity: 0
    },
    onClick: () => {
      setPreviousMatch(matchQueryResult.data);
      setMatchId(voteResult.data.nextMatchId);
      voteResult.reset();
    }
  }))) : /*#__PURE__*/React.createElement(ExpandedSpinner, null);
}
export default PlayView;